// src/components/ScoreboardAdmin/DuoScoreboardAdmin.jsx
import React, { useEffect, useMemo, useState } from "react";
import socket from "../../components/ScoreboardAdmin/socketClient";

const TECH_COLUMNS = ["A1", "A2", "B2", "B3", "C3", "C4"];

// helpers
const clampHalf = (v, min = 0, max = 10) => {
  const n = Number(v);
  if (Number.isNaN(n)) return 0;
  const r = Math.round(n * 2) / 2;
  return Math.max(min, Math.min(max, r));
};
const sum = (arr) => arr.reduce((a, b) => a + b, 0);
const initScores = () =>
  TECH_COLUMNS.reduce((m, k) => ({ ...m, [k]: [0, 0, 0, 0, 0] }), {});
// Afficher 0.5 près (ex: 5.5 reste 5.5) et "–" si 0
const fmtCell = (n) => {
  if (!n) return "–"; // garde le tiret pour 0 / vide
  const v = Math.round(n * 10) / 10; // une décimale
  return Number.isInteger(v) ? String(v) : v.toFixed(1);
  // Variante FR (virgule) :
  // return v.toLocaleString('fr-FR', { minimumFractionDigits: Number.isInteger(v) ? 0 : 1, maximumFractionDigits: 1 });
};

// Catégories d’âge et séries visibles
const AGE_OPTIONS = [
  "Avant-poussins",
  "Poussins/Benjamins/Minimes",
  "Cadets/Juniors/Séniors",
  "Vétérans",
];

// mapping simple demandé : A / A+B / A+B+C
const AGE_SERIES = {
  "Avant-poussins": ["A"],
  "Poussins/Benjamins/Minimes": ["A", "B"],
  "Cadets/Juniors/Séniors": ["A", "B", "C"],
  Vétérans: ["A", "B", "C"],
};

export default function DuoScoreboardAdmin({
  competiteur1, // ROUGE (haut)
  competiteur2, // BLEU  (bas)
  onValidate, // callback vers CombatModal
  tapisId, // numéro de tatami
  competitionName, // nom de l'événement
  competitionId,
}) {
  const [currentTech, setCurrentTech] = useState(TECH_COLUMNS[0]);
  const [activeTeam, setActiveTeam] = useState("red"); // "red" | "blue"

  // Catégorie d’âge (par défaut : cad/jun/sen)
  const [ageGroup, setAgeGroup] = useState("Cadets/Juniors/Séniors");

  // notes P1..P5 par technique
  const [redScores, setRedScores] = useState(initScores);
  const [blueScores, setBlueScores] = useState(initScores);

  // variété (VRT) — limité 0..2
  const [vrtRed, setVrtRed] = useState(0);
  const [vrtBlue, setVrtBlue] = useState(0);

  // pénalités -4
  const [penaltyRed, setPenaltyRed] = useState(false);
  const [penaltyBlue, setPenaltyBlue] = useState(false);

  // manches terminées (pour surbrillance des colonnes)
  const [finishedRed, setFinishedRed] = useState({});
  const [finishedBlue, setFinishedBlue] = useState({});

  const [isLocked, setIsLocked] = useState(false);

  // colonnes visibles selon la catégorie
  const visibleColumns = useMemo(() => {
    const allowed = new Set(AGE_SERIES[ageGroup] || ["A", "B", "C"]);
    return TECH_COLUMNS.filter((c) => allowed.has(c[0])); // c[0] = "A"|"B"|"C"
  }, [ageGroup]);

  const combatRoom = useMemo(
    () => (competitionId && tapisId ? `${competitionId}_${tapisId}` : null),
    [competitionId, tapisId]
  );

  useEffect(() => {
    if (!combatRoom) return;
    socket.emit("join_combat", combatRoom);
    // console.log("📡 Duo Admin rejoint :", combatRoom);
  }, [combatRoom]);

  useEffect(() => {
    if (!combatRoom) return;

    const onJudgeScore = ({ combatId, judgeIdx, tech, team, value }) => {
      if (combatId !== combatRoom) return;
      if (tech !== currentTech || team !== activeTeam) return;

      const set = team === "red" ? setRedScores : setBlueScores;
      set((prev) => {
        const next = {
          ...prev,
          [tech]: (prev[tech] || [0, 0, 0, 0, 0]).slice(),
        };
        next[tech][judgeIdx] = value;
        return next;
      });
    };

    socket.on("duo_judge_score", onJudgeScore);
    return () => socket.off("duo_judge_score", onJudgeScore);
  }, [combatRoom, currentTech, activeTeam]);

  // déverrouille quand tu changes de manche/équipe
  useEffect(() => {
    setIsLocked(false);
  }, [currentTech, activeTeam]);

  // broadcast de l’état courant
  useEffect(() => {
    if (!combatRoom) return;
    const state = {
      currentTech,
      activeTeam,
      ageGroup,
      visibleColumns,
      locked: isLocked,
    };
    socket.emit("duo_state_update", { combatId: combatRoom, state });
  }, [combatRoom, currentTech, activeTeam, ageGroup, visibleColumns, isLocked]);

  // si on change la catégorie et que la manche courante n’existe plus, on se replace
  useEffect(() => {
    if (!visibleColumns.includes(currentTech)) {
      setCurrentTech(visibleColumns[0] || TECH_COLUMNS[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleColumns]);

  // totaux par colonne (en fonction des colonnes visibles)
  const redCols = useMemo(
    () => visibleColumns.map((t) => sum(redScores[t] || [0, 0, 0, 0, 0])),
    [redScores, visibleColumns]
  );
  const blueCols = useMemo(
    () => visibleColumns.map((t) => sum(blueScores[t] || [0, 0, 0, 0, 0])),
    [blueScores, visibleColumns]
  );

  // totaux globaux
  const redTotalRaw =
    redCols.reduce((a, b) => a + b, 0) + (vrtRed || 0) - (penaltyRed ? 4 : 0);
  const blueTotalRaw =
    blueCols.reduce((a, b) => a + b, 0) +
    (vrtBlue || 0) -
    (penaltyBlue ? 4 : 0);

  const redTotalBig = Math.round(redTotalRaw * 10) / 10;
  const blueTotalBig = Math.round(blueTotalRaw * 10) / 10;
  const redTotCell = Math.round(redTotalRaw);
  const blueTotCell = Math.round(blueTotalRaw);
  const redRank = redTotalBig >= blueTotalBig ? "" : "";
  const blueRank = blueTotalBig > redTotalBig ? "" : "";

  // remonte au parent
  useEffect(() => {
    const result1 =
      redTotalBig > blueTotalBig ? "V" : redTotalBig < blueTotalBig ? "D" : "E";
    const result2 =
      redTotalBig < blueTotalBig ? "V" : redTotalBig > blueTotalBig ? "D" : "E";
    onValidate?.({
      points1: redTotalBig,
      points2: blueTotalBig,
      result1,
      result2,
      forfait1: false,
      forfait2: false,
      hansoku1: false,
      hansoku2: false,
      meta: {
        allTechs: TECH_COLUMNS,
        visibleTechs: visibleColumns,
        currentTech,
        activeTeam,
        redScores,
        blueScores,
        vrtRed,
        vrtBlue,
        penaltyRed,
        penaltyBlue,
        finishedRed,
        finishedBlue,
        ageGroup,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    redTotalBig,
    blueTotalBig,
    currentTech,
    activeTeam,
    redScores,
    blueScores,
    vrtRed,
    vrtBlue,
    penaltyRed,
    penaltyBlue,
    finishedRed,
    finishedBlue,
    ageGroup,
    visibleColumns,
  ]);

  useEffect(() => {
    if (!combatRoom) return;

    const data = {
      mode: "duo",
      currentTech,
      activeTeam,
      ageGroup,
      visibleColumns,
      redScores,
      blueScores,
      vrtRed,
      vrtBlue,
      penaltyRed,
      penaltyBlue,
      finishedRed,
      finishedBlue,
      redTotal: redTotalBig,
      blueTotal: blueTotalBig,
      competiteur1,
      competiteur2,
      tapisId,
      competitionName,
    };

    socket.emit("score_update", { combatId: combatRoom, data });
  }, [
    combatRoom,
    currentTech,
    activeTeam,
    ageGroup,
    visibleColumns,
    redScores,
    blueScores,
    vrtRed,
    vrtBlue,
    penaltyRed,
    penaltyBlue,
    finishedRed,
    finishedBlue,
    redTotalBig,
    blueTotalBig,
    competiteur1,
    competiteur2,
    tapisId,
    competitionName,
  ]);

  // MAJ d'une note
  const updateJudge = (team, judgeIdx, value) => {
    const v = clampHalf(value);
    const set = team === "red" ? setRedScores : setBlueScores;
    set((state) => {
      const next = { ...state, [currentTech]: state[currentTech].slice() };
      next[currentTech][judgeIdx] = v;
      return next;
    });
  };

  const markMancheDone = () => {
    if (activeTeam === "red") {
      setFinishedRed((s) => ({ ...s, [currentTech]: true }));
    } else {
      setFinishedBlue((s) => ({ ...s, [currentTech]: true }));
    }

    setIsLocked(true);
    if (combatRoom) {
      socket.emit("duo_lock_manche", {
        combatId: combatRoom,
        tech: currentTech,
        team: activeTeam,
        locked: true,
      });
    }
  };

  const HeaderBar = () => (
    <div className="px-3 py-2 bg-neutral-900 border-b border-neutral-800 flex items-center gap-2">
      <span className="opacity-80">{`Tatami ${tapisId ?? "—"}`}</span>
      <span className="opacity-50">|</span>
      <span className="font-semibold uppercase">
        {competitionName || "JJIF DUO — Open"}
      </span>

      <div className="flex-1" />

      {/* Select catégorie d'âge */}
      <label className="mr-2 text-[12px] opacity-80">Catégorie :</label>
      <select
        className="bg-neutral-800 border border-neutral-700 rounded px-2 py-1 text-[12px]"
        value={ageGroup}
        onChange={(e) => setAgeGroup(e.target.value)}
      >
        {AGE_OPTIONS.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    </div>
  );

  const PadLinks = () => {
  if (!combatRoom) return null;
  const makeUrl = (j) =>
    `${window.location.origin}/judge?room=${encodeURIComponent(combatRoom)}&j=${j}`;
  return (
    <div className="mt-2 bg-neutral-900 border border-neutral-800 rounded p-3">
      <div className="font-semibold mb-2">Connexion des juges (ouvrir dans un nouvel onglet)</div>
      <div className="flex flex-wrap gap-2">
        {[0,1,2,3,4].map((j) => (
          <button
            key={j}
            onClick={() => window.open(makeUrl(j), "_blank", "noopener,noreferrer")}
            className="px-3 py-2 bg-neutral-800 hover:bg-neutral-700 rounded border border-neutral-700"
          >
            Ouvrir Pad P{j + 1}
          </button>
        ))}
      </div>
    </div>
  );
};

  const FlagFR = () => (
    <div className="flex items-center gap-2 mt-1 text-[12px]">
      <div className="w-6 h-4 flex overflow-hidden rounded-[2px] border border-neutral-700">
        <div className="w-1/3 h-full bg-blue-600" />
        <div className="w-1/3 h-full bg-white" />
        <div className="w-1/3 h-full bg-red-600" />
      </div>
      <span className="opacity-90">FRA</span>
    </div>
  );

  const TeamHeader = ({ color, totalBig, rank, nameLine1, club }) => (
    <div
      className="w-full grid"
      style={{ gridTemplateColumns: "minmax(33vw,36vw) 1fr" }}
      onClick={() => setActiveTeam(color)}
    >
      {/* gros cartouche couleur (≥ 1/3) */}
      <div
        className={`flex items-center justify-center h-[96px] sm:h-[110px] md:h-[130px] lg:h-[150px] ${
          color === "red" ? "bg-red-700" : "bg-blue-700"
        } text-white rounded-tr-sm rounded-br-sm select-none`}
      >
        <span
          className="leading-none font-extrabold tabular-nums"
          style={{ fontSize: "clamp(2.2rem, 6.2vw, 4.2rem)" }}
        >
          {totalBig.toLocaleString("fr-FR", {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
          })}{" "}
          <span
            className="align-top"
            style={{ fontSize: "clamp(0.9rem, 2.2vw, 1.6rem)" }}
          >
            {rank}
          </span>
        </span>
      </div>

      {/* bloc identités */}
      <div className="bg-neutral-900 border border-neutral-800 px-3 py-2">
        <div
          className="uppercase font-extrabold truncate"
          style={{ fontSize: "clamp(0.9rem, 2.1vw, 1.25rem)" }}
        >
          {nameLine1 || "—"}
        </div>
        <div
          className="font-semibold truncate opacity-90"
          style={{ fontSize: "clamp(0.8rem, 1.8vw, 1rem)" }}
        >
          {club || "—"}
        </div>
        <FlagFR />
      </div>
    </div>
  );

  const TeamMatrix = ({
    color,
    colsValues,
    vrtValue,
    setVrt,
    totCell,
    finishedMap,
    penaltyValue,
    setPenaltyValue,
  }) => {
    const colsCount = visibleColumns.length;

    return (
      <div className="mt-2">
        {/* en-têtes colonnes */}
        <div
          className="grid text-[16px] bg-neutral-900 border border-neutral-800"
          style={{
            gridTemplateColumns: `repeat(${colsCount}, minmax(70px,1fr)) minmax(70px,0.7fr) minmax(90px,0.8fr)`,
          }}
        >
          {visibleColumns.map((t) => (
            <div
              key={`${color}-h-${t}`}
              className={`px-2 py-2 text-center font-semibold text-xl cursor-pointer ${
                currentTech === t && activeTeam === color
                  ? "bg-neutral-700"
                  : ""
              }`}
              onClick={() => {
                setCurrentTech(t);
                setActiveTeam(color);
              }}
            >
              {t}
            </div>
          ))}
          <div className="px-2 py-2 text-center font-semibold text-xl">VRT</div>
          <div className="px-2 py-2 text-center font-semibold text-xl">TOT</div>
        </div>

        {/* valeurs ligne */}
        <div
          className="grid text-xl font-bold border border-neutral-800"
          style={{
            gridTemplateColumns: `repeat(${colsCount}, minmax(70px,1fr)) minmax(70px,0.7fr) minmax(90px,0.8fr)`,
          }}
        >
          {colsValues.map((v, i) => {
            const tech = visibleColumns[i];
            const isDone = !!finishedMap?.[tech];
            const doneBg =
              color === "red"
                ? "bg-red-900/40 ring-1 ring-red-700"
                : "bg-blue-900/40 ring-1 ring-blue-700";
            return (
              <div
                key={`${color}-c-${i}`}
                className={`px-2 py-2 text-center tabular-nums ${
                  isDone ? doneBg : ""
                }`}
              >
                {fmtCell(v)}
              </div>
            );
          })}
          <div className="px-2 py-2 text-center">
            <input
              type="number"
              min={0}
              max={2}
              step={1}
              value={vrtValue}
              onChange={(e) => {
                const raw = Number(e.target.value || 0);
                const clamped = Math.max(0, Math.min(2, Math.round(raw)));
                setVrt(clamped);
              }}
              className="w-16 text-center bg-neutral-800 border border-neutral-700 rounded px-1 py-1"
            />
          </div>
          <div className="px-2 py-2">
            <div
              className={`rounded text-right px-2 py-1 font-extrabold tabular-nums ${
                color === "red" ? "bg-red-700" : "bg-blue-700"
              }`}
              style={{ fontSize: "clamp(0.95rem, 2vw, 1.15rem)" }}
            >
              {totCell}
            </div>
          </div>
        </div>

        {/* pénalité -4 */}
        <div className="flex items-center justify-end gap-3 mt-2">
          <label className="flex items-center gap-3 text-[14px]">
            <input
              type="checkbox"
              checked={penaltyValue}
              onChange={(e) => setPenaltyValue(e.target.checked)}
              className={`w-5 h-5 rounded border-2 bg-neutral-800 shadow-sm 
        ${
          penaltyValue
            ? "border-red-500 accent-red-600"
            : "border-neutral-500 accent-neutral-400"
        }
        focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500`}
            />
            <span
              className={`font-medium ${
                penaltyValue ? "text-red-400" : "text-neutral-300"
              }`}
            >
              Pénalité -4 pts
            </span>
            <span
              className={`px-2 py-0.5 text-xs rounded
        ${
          penaltyValue
            ? "bg-red-700 text-white"
            : "bg-neutral-800 text-neutral-400 border border-neutral-600"
        }`}
            >
              -4
            </span>
          </label>
        </div>
      </div>
    );
  };

  const ManchePanel = () => {
    const color = activeTeam;
    const scores = color === "red" ? redScores : blueScores;
    const setScores = color === "red" ? setRedScores : setBlueScores;

    const setJudge = (idx, val) => {
      const v = clampHalf(val);
      setScores((state) => {
        const next = { ...state, [currentTech]: state[currentTech].slice() };
        next[currentTech][idx] = v;
        return next;
      });
    };

    const palette =
      color === "red"
        ? { bg: "bg-red-900/40", border: "border-red-700" }
        : { bg: "bg-blue-900/40", border: "border-blue-700" };

    return (
      <div className="mt-4 border border-neutral-800 bg-neutral-900">
        {/* barre titre + switch équipe */}
        <div className="flex items-center justify-between px-3 py-2">
          <div className="text-[13px] uppercase">
            Manche&nbsp;<span className="font-bold">{currentTech}</span>
          </div>
          <div className="flex gap-1">
            <button
              className={`px-3 py-1 text-xl rounded-l border ${
                activeTeam === "red"
                  ? "bg-red-700 border-red-600"
                  : "bg-neutral-800 border-neutral-700 hover:bg-neutral-700 text-xl"
              }`}
              onClick={() => setActiveTeam("red")}
            >
              Rouge
            </button>
            <button
              className={`px-3 py-1 text-xl rounded-r border ${
                activeTeam === "blue"
                  ? "bg-blue-700 border-blue-600"
                  : "bg-neutral-800 border-neutral-700 hover:bg-neutral-700"
              }`}
              onClick={() => setActiveTeam("blue")}
            >
              Bleu
            </button>
          </div>
        </div>

        {/* tableau P1..P5 qui tient toute la largeur */}
        <div
          className="grid border-t border-neutral-800"
          style={{
            gridTemplateColumns:
              "minmax(82px, 120px) repeat(5, minmax(90px, 1fr))",
          }}
        >
          {/* header */}
          <div className="px-2 py-2 text-center font-semibold text-xl bg-neutral-800">
            {currentTech}
          </div>
          {["P1", "P2", "P3", "P4", "P5"].map((p) => (
            <div
              key={`h-${p}`}
              className="px-2 py-2 text-center font-semibold text-xl bg-neutral-800"
            >
              {p}
            </div>
          ))}

          {/* inputs */}
          <div className="px-2 py-2 text-center font-semibold text-xl bg-neutral-900">
            &nbsp;
          </div>
          {scores[currentTech].map((val, i) => (
            <div key={`c-${i}`} className="px-2 py-2">
              <input
                type="number"
                step={0.5}
                min={0}
                max={10}
                value={val}
                onChange={(e) => setJudge(i, e.target.value)}
                className={`w-full text-center text-xl font-bold border rounded px-2 py-2 ${palette.bg} ${palette.border}`}
              />
            </div>
          ))}
        </div>

        <div className="flex items-center justify-end gap-2 px-3 py-3 border-t border-neutral-800">
          <button
            className="px-3 py-2 text-xl bg-neutral-800 hover:bg-neutral-700 rounded border border-neutral-700"
            onClick={() => {
              // reset des 5 notes de la manche
              (color === "red" ? setRedScores : setBlueScores)((state) => {
                const next = { ...state };
                next[currentTech] = [0, 0, 0, 0, 0];
                return next;
              });
            }}
          >
            Réinitialiser la manche
          </button>
          <button
            className={`px-3 py-2 text-xl rounded ${
              color === "red"
                ? "bg-red-700 hover:bg-red-600"
                : "bg-blue-700 hover:bg-blue-600"
            }`}
            onClick={markMancheDone}
          >
            Valider la manche
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="w-screen h-screen bg-black text-white flex flex-col">
      <HeaderBar />
      <PadLinks />
      <div className="flex-1 overflow-auto p-2 sm:p-3 lg:p-4">
        {/* ===== ROUGE ===== */}
        <TeamHeader
          color="red"
          totalBig={redTotalBig}
          rank={redRank}
          nameLine1={
            competiteur1
              ? `${competiteur1.nom_competiteur || ""} ${
                  competiteur1.prenom_competiteur || ""
                }`.trim()
              : "—"
          }
          club={competiteur1?.club_competiteur || ""}
        />
        <TeamMatrix
          color="red"
          colsValues={redCols}
          vrtValue={vrtRed}
          setVrt={setVrtRed}
          totCell={redTotCell}
          finishedMap={finishedRed}
          penaltyValue={penaltyRed}
          setPenaltyValue={setPenaltyRed}
        />

        {/* espacement entre blocs */}
        <div className="h-6" />

        {/* ===== BLEU ===== */}
        <TeamHeader
          color="blue"
          totalBig={blueTotalBig}
          rank={blueRank}
          nameLine1={
            competiteur2
              ? `${competiteur2.nom_competiteur || ""} ${
                  competiteur2.prenom_competiteur || ""
                }`.trim()
              : "—"
          }
          club={competiteur2?.club_competiteur || ""}
        />
        <TeamMatrix
          color="blue"
          colsValues={blueCols}
          vrtValue={vrtBlue}
          setVrt={setVrtBlue}
          totCell={blueTotCell}
          finishedMap={finishedBlue}
          penaltyValue={penaltyBlue}
          setPenaltyValue={setPenaltyBlue}
        />

        {/* espacement */}
        <div className="h-6" />

        {/* ===== MANCHE (unique, pour l'équipe active) ===== */}
        <ManchePanel />
      </div>
    </div>
  );
}
